<template>
  <div
    ref="parentRef"
    :class="[$style.container, { [$style.pageMode]: pageMode }]"
    :style="{ gridAutoFlow: horizontal ? 'column' : 'row' }"
  >
    <slot name="before" />
    <div
      :class="$style.list"
      :style="listStyle.list"
    >
      <ul
        :class="$style.virtualList"
        :style="listStyle.virtual"
      >
        <slot
          name="virtualItems"
          :virtual-items="virtualItems"
          :virtual-index-set="virtualIndexSet"
          :measure="measureElement"
        >
          <li
            v-for="virtualItem in virtualItems"
            :key="virtualItem.key as string"
            :ref="measureElement"
            :class="$style.virtualItem"
            :data-index="virtualItem.index"
          >
            <slot
              name="item"
              :index="virtualItem.index"
              :item="items[virtualItem.index]"
            />
          </li>
        </slot>
      </ul>
    </div>
    <slot name="after" />
  </div>
</template>

<script setup lang="ts" generic="Items extends any[] = []">
import type { VirtualItem } from '@tanstack/vue-virtual';
import { useVirtualizer } from '@tanstack/vue-virtual';
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { getOverflowAncestors } from '@floating-ui/dom';
import { useMutationObserver } from '@vueuse/core';

// Component interface
type Props = {
  items: Items;
  pageMode?: boolean;
  activeIndex?: number | null;
  gap?: number;
  horizontal?: boolean;
  scrollToActiveIndex?: boolean;
};

const {
  pageMode,
  items,
  activeIndex = null,
  scrollToActiveIndex = true,
  horizontal = false,
  gap = 8
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  scroll: [scrollOffset: number | null];
}>();

defineSlots<{
  before: () => any;
  item: (props: {
    item: Items[number];
    index: number;
  }) => any;
  virtualItems: (props: {
    virtualItems: VirtualItem[];
    virtualIndexSet: Set<number>;
    measure: (el: any) => void;
  }) => any;
  after: () => any;
}>();

const onChange = ({ scrollOffset }: { scrollOffset: number | null }) => {
  emit('scroll', scrollOffset);
};

// Virtual List setup
const parentRef = useTemplateRef<HTMLElement>('parentRef');
const scrollMargin = ref(0);

const scrollableRoot = computed(() => {
  const container = parentRef.value;
  if (pageMode && container) {
    const list = getOverflowAncestors(container) as HTMLElement[];
    return list[0];
  }
  return container;
});
useMutationObserver(scrollableRoot, () => {
  if (parentRef.value && parentRef.value.offsetParent !== null) {
    scrollMargin.value = pageMode ? parentRef.value.offsetTop : 0;
  }
}, {
  childList: true,
  subtree: true
});

// В pageMode при пересоздании списка меняется высота страницы из за чего может скрол дёрнуть вверх
// Это что бы такого не было, бага была замечена при использовании с табами
if (pageMode) {
  watch(scrollableRoot, (el) => {
    if (!el) {
      return;
    }
    const savedTop = el.scrollTop;
    requestAnimationFrame(() => {
      if (el.scrollTop !== savedTop) {
        el.scrollTop = savedTop;
      }
    });
  });
}

const virtualOptions = computed(() => ({
  count: items.length,
  getScrollElement: () => scrollableRoot.value,
  horizontal,
  overscan: 2,
  scrollMargin: scrollMargin.value,
  estimateSize: () => 60,
  gap,
  onChange
}));

const listVirtualizer = useVirtualizer(virtualOptions);

const virtualItems = computed(() => listVirtualizer.value.getVirtualItems());
const virtualIndexSet = computed(() => new Set(virtualItems.value.map((vi) => vi.index)));
const totalSize = computed(() => listVirtualizer.value.getTotalSize());
const measureElement = (el) => {
  if (!el) {
    return;
  }
  listVirtualizer.value.measureElement(el);
  return undefined;
};

// Styles
const listStyle = computed(() => {
  const h = horizontal;
  const height = h ? '100%' : `${totalSize.value}px`;
  const width = h ? `${totalSize.value}px` : '100%';
  const transform = `translate${h ? 'X' : 'Y'}(${(virtualItems.value[0]?.start - scrollMargin.value)}px)`;

  return {
    list: {
      height,
      width
    },
    virtual: {
      transform,
      gap: `${gap}px`,
      width: h ? '' : '100%',
      height: h ? '100%' : '',
      flexDirection: h ? 'row' : 'column'
    }
  };
});

// Functions
const scrollToIndex = (index: number, smooth?: boolean) => {
  listVirtualizer.value.scrollToIndex(index, {
    align: 'center',
    behavior: smooth ? 'smooth' : 'auto'
  });
};

const scrollToTop = (smooth?: boolean) => {
  listVirtualizer.value.scrollToOffset(0, { behavior: smooth ? 'smooth' : 'auto' });
};
const scrollToBottom = (smooth?: boolean) => {
  listVirtualizer.value.scrollToOffset(totalSize.value, { behavior: smooth ? 'smooth' : 'auto' });
};

watch(() => activeIndex, (index) => {
  if (index === null || !scrollToActiveIndex) {
    return;
  }
  scrollToIndex(index);
});

onMounted(() => {
  setTimeout(() => {
    if (activeIndex === null) {
      return;
    }
    scrollToIndex(activeIndex);
  }, 0);
});

defineExpose({
  scrollToIndex,
  scrollToTop,
  scrollToBottom
});
</script>

<style module>
.container {
  height: 100%;
  overflow: auto;
  contain: none;
  overflow-anchor: none;
  display: grid;
  grid-template-rows: max-content;
}
.pageMode{
  overflow: unset;
}
.list{
  position: relative;
}
.virtualList {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  margin: 0;
  padding: 0;
}
.virtualItem{
  list-style: none;
  display: grid;
}
</style>

<i18n lang="json">{}</i18n>
